import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ContactLogo from '../components/shared/ContactLogo';
import HolographicBeams from '../components/ui/beams-background';

const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const IconScan = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 8V6a2 2 0 012-2h2M4 16v2a2 2 0 002 2h2M20 8V6a2 2 0 00-2-2h-2M20 16v2a2 2 0 01-2 2h-2"/>
    <path d="M4 12h16" strokeDasharray="2.5 3"/>
  </svg>
);
const IconOrganize = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="8" r="3"/>
    <path d="M3.5 19c0-3 2.5-5.2 5.5-5.2s5.5 2.2 5.5 5.2"/>
    <circle cx="17.5" cy="7.5" r="2.1"/>
    <path d="M15.8 13.7c2.3.4 3.9 2.2 3.9 5"/>
  </svg>
);
const IconRemember = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9"/>
    <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
  </svg>
);
const IconActivate = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <rect x="3" y="13" width="4" height="8" rx="1.2"/>
    <rect x="10" y="8" width="4" height="13" rx="1.2"/>
    <rect x="17" y="3" width="4" height="18" rx="1.2"/>
  </svg>
);

const WAITLIST_URL = 'https://contactnetwork.cl/app/waitlist';

const BENEFITS = [
  { title: 'Escanea', desc: 'Tarjetas y perfiles en segundos.', icon: <IconScan/> },
  { title: 'Organiza', desc: 'Tus contactos profesionales fácilmente.', icon: <IconOrganize/> },
  { title: 'Recuerda', desc: 'Lo importante de cada reunión.', icon: <IconRemember/> },
  { title: 'Activa', desc: 'Oportunidades con seguimiento.', icon: <IconActivate/> },
];

const MOCKUP_URL = 'https://res.cloudinary.com/dfyzvch4i/image/upload/c_crop,x_396,y_37,w_1119,h_1032/f_auto,q_auto/v1787629158/mockups_app_iwnlhe.png';

/* Public landing for the upcoming Contact mobile app. Mirrors Homepage.js's
   navbar and design tokens so it feels like the same site — see Homepage.js
   for the canonical versions of these pieces. This page is Spanish-only (no
   other standalone page in this app supports the ES/EN/PT switcher either). */
const AppPage = () => {
  const navigate = useNavigate();
  const [navBorder, setNavBorder] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [heroReady, setHeroReady] = useState(true);

  useEffect(() => {
    const fn = () => setNavBorder(window.scrollY > 24);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Space Grotesk is only used on this page — loaded here instead of
  // globally in index.html so every other route doesn't pay for a font it
  // never renders.
  useEffect(() => {
    const id = 'apx-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);

  return (
    <div className="nx-root">
      <style>{CSS}</style>

      {/* ── NAV (misma estructura que Homepage.js) ── */}
      <header className={`nx-nav ${navBorder ? 'nx-nav--scrolled' : ''}`}>
        <div className="nx-nav-inner">
          <div className="nx-nav-left">
            <button className="nx-hamburger" onClick={() => setMobileNavOpen(true)} aria-label="Abrir menú">
              <span/><span/><span/>
            </button>
            <ContactLogo clickable />
          </div>
          <nav className="nx-nav-links">
            <a href="/#caracteristicas" className="nx-nav-link">Características</a>
            <a href="/#precios"         className="nx-nav-link">Planes</a>
            <a href="/#nosotros"        className="nx-nav-link">Solución</a>
            <a href="/app"              className="nx-nav-link">App</a>
          </nav>
          <div className="nx-nav-right">
            <button onClick={() => navigate('/login')} className="nx-btn-ghost">Iniciar sesión</button>
            <button onClick={() => navigate('/comprar')} className="nx-btn-outline">Comprar</button>
            <button onClick={() => navigate('/login')} className="nx-btn-acceder">Acceder <IconArrowRight/></button>
            <button onClick={() => navigate('/cotizar')} className="nx-btn-pill">Cotizar <IconArrowRight/></button>
          </div>
        </div>
      </header>

      {mobileNavOpen && (
        <div className="nx-mnav-overlay" onClick={() => setMobileNavOpen(false)}>
          <div className="nx-mnav-panel" onClick={e => e.stopPropagation()}>
            <div className="nx-mnav-head">
              <ContactLogo/>
              <button className="nx-mnav-close" onClick={() => setMobileNavOpen(false)} aria-label="Cerrar menú">
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <a href="/#caracteristicas" className="nx-mnav-link" onClick={() => setMobileNavOpen(false)}>Características</a>
            <a href="/#precios"         className="nx-mnav-link" onClick={() => setMobileNavOpen(false)}>Planes</a>
            <a href="/#nosotros"        className="nx-mnav-link" onClick={() => setMobileNavOpen(false)}>Solución</a>
            <a href="/app"              className="nx-mnav-link" onClick={() => setMobileNavOpen(false)}>App</a>
          </div>
        </div>
      )}

      {/* ── HERO ── */}
      <section className="apx-hero">
        <HolographicBeams density={22} speed={0.9} aberration={2} opacity={55} />
        {heroReady && (
          <div className="nx-container">
            <div className="apx-hero-grid">

              <div className="apx-hero-left">
                <div className="nx-load" style={{ animationDelay: '110ms' }}>
                  <h1 className="apx-title">
                    Tu red profesional,<br/>
                    <span className="apx-title-accent">ahora en tu bolsillo.</span>
                  </h1>
                </div>

                <div className="nx-load" style={{ animationDelay: '200ms' }}>
                  <p className="apx-subtitle">La app de Contact te ayuda a capturar, organizar y activar cada contacto que conoces.</p>
                </div>

                <div className="nx-load" style={{ animationDelay: '290ms' }}>
                  <div className="apx-benefits">
                    {BENEFITS.map((b) => (
                      <div key={b.title} className="apx-benefit">
                        <span className="apx-benefit-icon">{b.icon}</span>
                        <p className="apx-benefit-title">{b.title}</p>
                        <p className="apx-benefit-desc">{b.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="nx-load" style={{ animationDelay: '380ms' }}>
                  <a href={WAITLIST_URL} className="nx-btn-pill nx-btn-pill--lg apx-cta">
                    Únete al acceso anticipado <IconArrowRight/>
                  </a>
                  <p className="apx-cta-note">Acceso anticipado y beneficios exclusivos para quienes se inscriban.</p>
                </div>

                <div className="nx-load" style={{ animationDelay: '440ms' }}>
                  <div className="apx-stores">
                    <img src="/appstore-badge-es.svg" alt="Descárgalo en el App Store" className="apx-store-badge" />
                    <img src="/googleplay-badge-es.png" alt="Disponible en Google Play" className="apx-store-badge apx-store-badge--google" />
                  </div>
                  <p className="apx-stores-note">Próximamente</p>
                </div>
              </div>

              <div className="apx-hero-right nx-load" style={{ animationDelay: '180ms' }}>
                <div className="apx-mockup-glow" aria-hidden="true"/>
                <div className="apx-mockup-ring" aria-hidden="true"/>
                <div className="apx-mockup-crop">
                  <img src={MOCKUP_URL} alt="App de Contact en el celular" className="apx-mockup-img" />
                </div>
                <div className="apx-mockup-shadow" aria-hidden="true"/>
              </div>

            </div>
          </div>
        )}
      </section>
    </div>
  );
};

const CSS = `
@font-face {
  font-family: 'abcNormal';
  src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

:root{
  --bg:#F9F9F8;
  --bg-w:#FFFFFF;
  --text:#000000;
  --muted:#4a4a4a;
  --faint:#C0C0C0;
  --border:#EEEEEC;
  --border-dk:#CCCCCA;
  --dark:#0F172A;
  --blue:#0071e3;
  --max:1200px;
  --r:10px;
}
*{box-sizing:border-box;margin:0;padding:0}
html{-webkit-overflow-scrolling:touch}
.nx-root{font-family:'abcNormal','Inter','Arial',sans-serif;background:var(--bg);color:var(--text);overflow-x:hidden;line-height:1.6;min-height:100vh}
button{font-family:'Inter',system-ui,sans-serif!important}
.nx-container{max-width:var(--max);margin:0 auto;padding:0 40px}

@media (min-width: 1600px) {
  :root{--max:1440px}
  .nx-nav{max-width:1400px}
}

@keyframes nxLoadIn{from{opacity:0;transform:translateY(40px) scale(.96)}to{opacity:1;transform:translateY(0) scale(1)}}
@keyframes nxNavIn{from{opacity:0;transform:translateY(-14px)}to{opacity:1;transform:translateY(0)}}
@keyframes nxMnavFade{from{opacity:0}to{opacity:1}}
@keyframes nxMnavSlide{from{transform:translateX(-100%)}to{transform:translateX(0)}}
.nx-load{animation:nxLoadIn .95s cubic-bezier(.22,1,.36,1) both}
.nx-nav{animation:nxNavIn .5s cubic-bezier(.22,1,.36,1) both}

/* ── BUTTONS ── */
.nx-btn-pill{display:inline-flex;align-items:center;gap:8px;background:var(--text);color:#fff;border:none;border-radius:100px;height:46px;padding:0 22px;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;text-decoration:none;transition:opacity .15s,transform .2s cubic-bezier(.22,1,.36,1),box-shadow .2s;white-space:nowrap}
.nx-btn-pill:hover{opacity:.82;transform:translateY(-2px);box-shadow:0 6px 20px rgba(59,130,246,.25)}
.nx-btn-pill:active{transform:scale(.94) translateY(0) !important;box-shadow:none !important;opacity:1}
.nx-btn-pill--lg{height:52px;font-size:15px;padding:0 28px}
.nx-btn-outline{display:inline-flex;align-items:center;gap:8px;background:transparent;color:var(--text);border:1.5px solid var(--border-dk);border-radius:100px;height:46px;padding:0 22px;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;transition:all .2s cubic-bezier(.22,1,.36,1);white-space:nowrap}
.nx-btn-outline:hover{border-color:var(--text);background:var(--bg-w);box-shadow:0 4px 14px rgba(59,130,246,.12)}
.nx-btn-ghost{background:none;border:none;color:var(--muted);font-family:inherit;font-size:14px;font-weight:500;cursor:pointer;padding:0 12px;height:44px;transition:color .15s}
.nx-btn-ghost:hover{color:var(--text)}
.nx-btn-acceder{display:none}

/* ── NAV (dark glass, tuned for the black /app hero) ── */
.nx-nav{position:fixed;top:16px;left:16px;right:16px;z-index:100;max-width:1160px;margin:0 auto;background:rgba(20,22,30,.5);backdrop-filter:blur(20px) saturate(180%);-webkit-backdrop-filter:blur(20px) saturate(180%);border:1px solid rgba(255,255,255,.1);border-radius:20px;box-shadow:0 4px 24px rgba(0,0,0,.25);transition:background .3s,box-shadow .3s,border-color .3s,top .3s}
.nx-nav--scrolled{background:rgba(12,13,18,.72);border-color:rgba(255,255,255,.14);box-shadow:0 8px 28px rgba(0,0,0,.35)}
.nx-nav-inner{max-width:100%;margin:0 auto;padding:0 22px;height:50px;display:flex;align-items:center;justify-content:space-between}
.nx-nav-left{display:flex;align-items:center;gap:14px}
.nx-nav-left .h-8{height:26px}
.nx-hamburger{display:none;flex-direction:column;justify-content:center;align-items:center;gap:4px;width:30px;height:30px;background:none;border:none;cursor:pointer;padding:0;flex-shrink:0}
.nx-hamburger span{display:block;width:17px;height:2px;border-radius:2px;background:#fff}
.nx-nav-links{display:flex;gap:24px;background:transparent;backdrop-filter:none;-webkit-backdrop-filter:none;border-bottom:none}
.nx-nav-link{position:relative;font-size:13px;font-weight:500;color:rgba(255,255,255,.7);text-decoration:none;transition:color .15s}
.nx-nav-link::after{content:'';position:absolute;bottom:-3px;left:0;width:0;height:1.5px;background:#fff;transition:width .25s cubic-bezier(.16,1,.3,1)}
.nx-nav-link:hover{color:#fff}
.nx-nav-link:hover::after{width:100%}
.nx-nav-right{display:flex;align-items:center;gap:6px}
.nx-nav-right .nx-btn-pill,.nx-nav-right .nx-btn-outline{height:36px;padding:0 16px;font-size:13px}
.nx-nav .nx-btn-ghost{color:rgba(255,255,255,.7)}
.nx-nav .nx-btn-ghost:hover{color:#fff}
.nx-nav .nx-btn-outline{color:#fff;border-color:rgba(255,255,255,.3)}
.nx-nav .nx-btn-outline:hover{border-color:#fff;background:rgba(255,255,255,.08);box-shadow:none}
.nx-nav .nx-btn-pill{background:#fff;color:#000}
.nx-nav .nx-btn-pill:hover{opacity:.85;box-shadow:0 6px 20px rgba(255,255,255,.15)}

.nx-mnav-overlay{position:fixed;inset:0;z-index:200;background:rgba(0,0,0,.6);backdrop-filter:blur(2px);display:flex;animation:nxMnavFade .2s ease}
.nx-mnav-panel{width:78%;max-width:300px;height:100%;background:#0a0a0f;box-shadow:4px 0 32px rgba(0,0,0,.5);display:flex;flex-direction:column;padding:24px 22px;animation:nxMnavSlide .28s cubic-bezier(.22,1,.36,1)}
.nx-mnav-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:32px}
.nx-mnav-close{width:32px;height:32px;border-radius:8px;background:rgba(255,255,255,.08);border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#fff;flex-shrink:0}
.nx-mnav-link{font-size:17px;font-weight:600;color:#fff;text-decoration:none;padding:16px 4px;border-bottom:1px solid rgba(255,255,255,.1)}
.nx-mnav-link:last-child{border-bottom:none}

/* ── APP PAGE HERO ── */
.apx-hero{position:relative;z-index:1;padding:104px 0 40px;overflow:hidden;min-height:100vh;display:flex;align-items:center;background:#000}
.apx-hero .nx-container{max-width:1320px}
.apx-hero-grid{display:grid;grid-template-columns:44% 56%;gap:32px;align-items:center;position:relative;z-index:1;width:100%}
.apx-hero-left{display:flex;flex-direction:column;align-items:flex-start;text-align:left;max-width:560px}
.apx-title{font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',system-ui,sans-serif;font-size:clamp(32px,3.9vw,58px);font-weight:600;line-height:1.08;letter-spacing:-.03em;color:#fff;margin-bottom:16px}
.apx-title-accent{background:linear-gradient(100deg,#7dd3fc 20%,#60a5fa 100%);-webkit-background-clip:text;background-clip:text;color:transparent}
.apx-subtitle{font-size:17px;color:rgba(255,255,255,.65);line-height:1.55;max-width:520px;margin:0 0 26px}
.apx-benefits{display:grid;grid-template-columns:1fr 1fr;gap:18px 28px;width:100%;max-width:520px;margin-bottom:26px}
.apx-benefit{display:flex;flex-direction:column;align-items:flex-start}
.apx-benefit-icon{display:inline-flex;color:#7dd3fc;margin-bottom:8px}
.apx-benefit-icon svg{width:22px;height:22px}
.apx-benefit-title{font-family:'Space Grotesk','Inter',system-ui,sans-serif;font-size:14px;font-weight:600;color:#fff;letter-spacing:-.01em;margin-bottom:2px}
.apx-benefit-desc{font-size:12px;color:rgba(255,255,255,.5);line-height:1.4}
.apx-cta{font-family:'Space Grotesk','Inter',system-ui,sans-serif;margin-bottom:10px;background:#fff;color:#000}
.apx-cta:hover{opacity:.85}
.apx-cta-note{font-size:12px;color:rgba(255,255,255,.45);max-width:420px}
.apx-stores{display:flex;align-items:center;gap:10px;margin-top:20px}
.apx-store-badge{height:40px;width:120px;flex:none;display:block}
.apx-store-badge--google{height:auto;width:155px}
.apx-stores-note{font-size:12px;color:rgba(255,255,255,.4);margin-top:8px}
.apx-hero-right{position:relative;z-index:1;height:min(70vh,625px);display:flex;align-items:center;justify-content:center}
.apx-mockup-glow{position:absolute;width:70%;height:70%;border-radius:50%;background:radial-gradient(circle,rgba(125,211,252,.22),rgba(125,211,252,0) 70%);filter:blur(30px);z-index:0}
.apx-mockup-ring{position:absolute;width:38px;height:38px;border-radius:50%;border:2px solid rgba(255,255,255,.4);top:6%;right:8%;z-index:2}
.apx-mockup-ring::after{content:'';position:absolute;width:8px;height:8px;border-radius:50%;background:#7dd3fc;top:-4px;left:-4px}
.apx-mockup-shadow{position:absolute;bottom:2%;width:56%;height:28px;border-radius:50%;background:radial-gradient(ellipse,rgba(125,211,252,.18),rgba(125,211,252,0) 72%);filter:blur(6px);z-index:0}
.apx-mockup-crop{position:relative;z-index:1;height:100%;aspect-ratio:1119/1032;max-width:100%}
.apx-mockup-img{width:100%;height:100%;object-fit:contain;object-position:50% 50%;display:block}

/* ── RESPONSIVE ── */
@media(max-width:1024px){
  .nx-container{padding:0 28px}
  .nx-nav-inner{padding:0 28px}
  .nx-nav{left:12px;right:12px;top:12px}
  .nx-nav-links{display:none}
  .nx-hamburger{display:flex}
  .apx-hero{min-height:0;display:block;padding:118px 0 48px}
  .apx-hero-grid{grid-template-columns:1fr;gap:12px}
  .apx-hero-left{align-items:flex-start}
  .apx-benefits{max-width:520px}
  .apx-hero-right{height:auto;margin-top:24px}
  .apx-mockup-crop{height:auto;width:min(640px,100%)}
  .apx-mockup-ring{display:none}
}
@media(max-width:640px){
  .nx-container{padding:0 22px}
  .nx-nav-inner{padding:0 14px;height:46px}
  .nx-nav{left:8px;right:8px;top:8px;border-radius:15px}
  .nx-nav-left .h-8{height:24px}
  .nx-btn-ghost{display:none}
  .nx-btn-acceder{display:inline-flex;align-items:center;background:var(--text);color:#fff;border:none;border-radius:100px;height:36px;padding:0 16px;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer;transition:opacity .15s}
  .nx-nav-right .nx-btn-pill{display:none}

  .apx-hero{padding:100px 0 48px}
  .apx-title{font-size:clamp(34px,10vw,44px);line-height:1.14}
  .apx-subtitle{font-size:15.5px;max-width:100%}
  .apx-benefits{grid-template-columns:1fr 1fr;gap:16px 14px;max-width:100%}
  .apx-cta{width:100%;justify-content:center}
  .apx-hero-right{margin-top:12px}
  .apx-mockup-crop{width:100vw;max-width:100vw;margin-left:calc(50% - 50vw);margin-right:calc(50% - 50vw)}
}
`;

export default AppPage;
